/* lp-numbers.js — the numbers band rolls up from zero when it scrolls into view.
   Ported 9/16 Sep 2026 from the prototype's count-up IIFE (grep COUNTMIN in
   website/Leatherpress_Website_v4_branch2.html). Behaviour identical; shape is a custom element so
   the theme editor's re-mount re-runs it.

   THE CURVE (Luke, 21 Aug: "first 10,000 really quickly, last 100 slower, reach the max a little
   faster"): exponential-out, v = N · (1 − 2^(−EXPO·k)), 1100ms. For 20,000: 0→10,000 in the first
   110ms, 19,900 at 840ms, the last 100 over the final 260ms. EXPO is the dial.

   TWO THINGS THAT LOOKED WRONG IN THE OBVIOUS VERSION
   · "1890s" counted through "412s", "873s" — a LETTER suffix is held back until the count lands;
     a symbol one ("+", "%") rides along, because "12,000+" is fine mid-roll.
   · "1" cannot roll — 0→1 shows "0 LEATHER COMPANY" for most of the run. Anything under COUNTMIN is
     simply present from the start.

   The markup stays the only source of truth: figures are parsed out of the text on the page. The
   corporate page's 1/2/3 band must NOT roll — that is the `countup` setting, off there. */
(function () {
  'use strict';
  if (!('customElements' in window) || customElements.get('lp-numbers')) return;
  var COUNTMIN = 3, DUR = 1100, EXPO = 10;

  function parse(txt) {
    var m = /^(\D*?)([\d.,]+)(.*)$/.exec((txt || '').trim());
    if (!m) return null;
    var n = parseFloat(m[2].replace(/,/g, ''));
    if (!isFinite(n)) return null;
    return { pre: m[1], n: n, suf: m[3], grouped: m[2].indexOf(',') > -1,
             during: /[A-Za-z]/.test(m[3]) ? '' : m[3] };   /* letters wait; symbols ride along */
  }
  function fmt(v, p, suf) {
    var s = String(Math.round(v));
    if (p.grouped) s = s.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    return p.pre + s + suf;
  }

  class LPNumbers extends HTMLElement {
    connectedCallback() {
      this.disconnectedCallback();
      var self = this;
      var reduced = !!(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches);
      var figures = Array.prototype.slice.call(this.querySelectorAll('h3'));
      /* the finished value is the accessible name throughout, so a screen reader never announces a
         half-counted figure */
      figures.forEach(function (h) { if (!h.getAttribute('aria-label')) h.setAttribute('aria-label', h.textContent.trim()); });
      if (!this.hasAttribute('data-countup')) return;           /* opt-in, as .numbers.countup was */

      function run() {
        figures.forEach(function (h) {
          var final = h.getAttribute('aria-label') || h.textContent;
          var p = parse(final); if (!p) return;
          if (reduced || p.n < COUNTMIN) { h.textContent = final; return; }
          h.textContent = fmt(0, p, p.during);
          var t0 = null;
          (function step(ts) {
            if (t0 === null) t0 = ts;
            var k = Math.min(1, (ts - t0) / DUR);
            h.textContent = k < 1 ? fmt(p.n * (1 - Math.pow(2, -EXPO * k)), p, p.during) : final;
            if (k < 1) self._raf = requestAnimationFrame(step);
          }(performance.now()));
        });
      }
      /* fires when the band arrives, on screen at load or reached by scrolling; unobserved after the
         first run so it cannot replay on the way back up. IntersectionObserver's root is the viewport,
         so Horizon's .page-wrapper scroller changes nothing here. */
      if (!('IntersectionObserver' in window)) { run(); return; }
      this._io = new IntersectionObserver(function (entries) {
        entries.forEach(function (e) {
          if (!e.isIntersecting) return;
          self._io.unobserve(e.target); run();
        });
      }, { threshold: 0.35 });
      this._io.observe(this);
    }
    disconnectedCallback() {
      if (this._io) { this._io.disconnect(); this._io = null; }
      if (this._raf) { cancelAnimationFrame(this._raf); this._raf = null; }
    }
  }
  customElements.define('lp-numbers', LPNumbers);
})();
